import { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { UserHasPermissionToRoute } from 'utils/auth/roles-and-permissions';
import { formatNumericValue } from 'utils/general';

import { IHome } from '@/modules/home/types';
import { CircularBgImage, Typography } from '@/shared/components/atoms';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { getUnitName } from '@/shared/lib/convert-units';

import Inbound from '~/images/shared/inbound.svg';
import Outbound from '~/images/shared/outbound.svg';

export const useHomeInputsUtils = () => {
  const { t } = useTranslation();

  const [isDemoFarm] = useStore((store) => store?.isDemoFarm);

  const canViewDispatch =
    UserHasPermissionToRoute(routes.dispatchRequests) && !isDemoFarm;

  const getTransactionsRequestsTableColumns = () => {
    const TransactionsTablecolumnHelper =
      createColumnHelper<IHome.IInventoryTransactions>();

    const transactionTypeStyles = {
      out: {
        bg: 'bg-error-80',
        color: 'text-tertiary-20',
        icon: <Outbound width='18' height='18' />,
      },
      in: {
        bg: 'bg-secondary-95',
        color: 'text-green-300',
        icon: <Inbound width='18' height='18' />,
      },
    };

    return [
      TransactionsTablecolumnHelper.accessor('productName', {
        header: t('Item'),
        cell: ({ getValue, row: { original } }) => (
          <div className='flex items-center gap-8'>
            <div
              className={classNames(
                'flex h-30 w-30 items-center justify-center rounded-full',
                transactionTypeStyles[original.type].bg
              )}
            >
              <span className={transactionTypeStyles[original.type].color}>
                {transactionTypeStyles[original.type].icon}
              </span>
            </div>
            <div>
              <Typography className='!text-14'>{getValue()}</Typography>
              <div className='flex items-center gap-4'>
                <Typography className='!text-12 text-surface-10'>
                  {original.categoryName}
                </Typography>
                <span className='text-12'>-</span>
                {original.type === 'out' && canViewDispatch ? (
                  <Link
                    className='text-12 text-primary-60 underline'
                    href={`${routes.dispatchRequests}/${original.requestId}`}
                  >
                    #{original.requestId}
                  </Link>
                ) : (
                  <Typography
                    variant='subtitle2'
                    className='!text-12 text-primary-60'
                  >{`#${original.requestId}`}</Typography>
                )}
              </div>
            </div>
          </div>
        ),
      }),
      TransactionsTablecolumnHelper.accessor('location', {
        header: t('location'),
        cell: ({ getValue }) => (
          <Typography
            variant='caption'
            weight='semiBold'
            className='flex items-center justify-center rounded-xl border-xs border-neutralVariant-90 px-12 py-4 !text-12'
          >
            {getValue()}
          </Typography>
        ),
      }),
      TransactionsTablecolumnHelper.accessor('productAmount', {
        header: t('batch-amount'),
        cell: ({ getValue, row: { original } }) => (
          <Typography weight='medium' className='!text-14'>{`${
            getValue() ? formatNumericValue(getValue()) : 0
          } ${t(getUnitName(original.productUnit))}`}</Typography>
        ),
      }),
    ];
  };

  const getPendingRequestsTableColumns = () => {
    const PendingTablecolumnHelper =
      createColumnHelper<IHome.IInventoryPendingRequest>();

    return [
      PendingTablecolumnHelper.accessor('productName', {
        header: t('Item'),
        cell: ({ getValue, row: { original } }) => (
          <div>
            <Typography>{getValue()}</Typography>
            <div className='flex items-center gap-4'>
              <Typography className='text-surface-10'>
                {original.categoryName}
              </Typography>
              <span>-</span>
              {canViewDispatch ? (
                <Link
                  className='text-primary-60 underline'
                  href={`${routes.dispatchRequests}/${original.dispatchRequestId}`}
                >
                  #{original.dispatchRequestId}
                </Link>
              ) : (
                <Typography
                  variant='subtitle2'
                  className='text-primary-60'
                >{`#${original.dispatchRequestId}`}</Typography>
              )}
            </div>
          </div>
        ),
      }),
      PendingTablecolumnHelper.accessor('plots', {
        header: t('location'),
        cell: ({ getValue }) => (
          <PartialListWithTooltip
            limit={1}
            items={getValue().map((item) => ({
              description: item.name,
              imageURL: item.cropImage,
            }))}
            imgProps={{
              size: 'sm',
              bgColor: 'bg-outline-default',
              imageClassName: 'scale-125',
            }}
          />
        ),
      }),
      PendingTablecolumnHelper.accessor('totalAmount', {
        header: t('batch-amount'),
        cell: ({ getValue, row: { original } }) => (
          <Typography weight='medium'>{`${formatNumericValue(getValue())} ${t(
            getUnitName(original.unit)
          )}`}</Typography>
        ),
      }),
    ];
  };

  const getPeriodicOperationsColumns = () => {
    const columnHelper = createColumnHelper<IHome.IPeriodicOperation>();
    return [
      columnHelper.accessor('descriptiveName', {
        header: t('Operation'),
        cell: ({ getValue, row: { original } }) => (
          <div className='flex items-center gap-8'>
            <CircularBgImage imageSrc={original.categoryIcon} size='md' />
            <div>
              <Typography className='!text-14'>{getValue()}</Typography>
              <div className='flex items-center gap-4'>
                <Link
                  className={classNames(
                    'text-12 text-primary-60',
                    isDemoFarm ? 'pointer-events-none' : 'underline'
                  )}
                  tabIndex={isDemoFarm ? -1 : undefined}
                  aria-disabled={isDemoFarm}
                  href={`${routes.periodicJobs}/${original.id}`}
                >
                  #{original.id}
                </Link>
                <span className='text-12'>-</span>
                <Typography className='!text-12 text-surface-10'>
                  {original.createdBy}
                </Typography>
              </div>
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('plots', {
        header: t('location'),
        cell: ({ getValue }) => (
          <PartialListWithTooltip
            limit={1}
            items={getValue().map((item) => ({
              description: item.name,
              imageURL: item.cropImage,
            }))}
          />
        ),
      }),
      columnHelper.accessor('products', {
        header: t('Items'),
        cell: ({ getValue }) => {
          const val = getValue();
          return val ? (
            <PartialListWithTooltip
              limit={1}
              items={val.map((item) => ({
                description: item,
              }))}
            />
          ) : (
            '-'
          );
        },
      }),
    ];
  };

  return {
    getTransactionsRequestsTableColumns,
    getPendingRequestsTableColumns,
    getPeriodicOperationsColumns,
  };
};
